// Payment System v3.0 PR 9 — Phoenix-Backed Resale wizard (design spec §8.5).
// Route: /portfolio-admin/loans/:loanId/resale. Three steps:
//   1. Context — loan summary + four inputs (closing date, new buyer name,
//      sale price, assumed amount with a one-click "use current balance"
//      helper).
//   2. Preview — server-computed Assumption Fee (2% × assumed), Spread Note
//      principal (sale − assumed), Bridge Period end date.
//   3. Confirm — final summary; Execute button POSTs and redirects to the
//      Spread Note's detail page.
//
// Distinct from RightSize: the existing loan is ASSUMED (not retired), a
// Spread Note is originated for the equity gain, and only the 2% Assumption
// Fee flows through WF1 — no YMP, no Prep, no Coord.

import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check } from "lucide-react";
import {
  Badge,
  Button,
  Card,
  FormField,
  Input,
} from "@/design-system/components/ui";
import {
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@/design-system/components/ui";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { PreContributionScopeRedirect } from "@/components/portfolioAdmin/PreContributionScopeRedirect";
import { useLoan } from "@/hooks/portfolioAdmin/usePortfolioAdmin";
import {
  useExecuteResale,
  usePreviewResale,
} from "@/hooks/portfolioAdmin/useResale";
import type { ResaleClosingPreview } from "@/types/resale";
import { isoDateInTz, useUserTimeZone } from "@/utils/datetime";

type Step = 1 | 2 | 3;

function formatCurrency(n: number): string {
  return n.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  });
}

export function ResaleWizard() {
  const { loanId } = useParams<{ loanId: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const loanQuery = useLoan(loanId);
  const preview = usePreviewResale(loanId ?? "");
  const execute = useExecuteResale(loanId ?? "");
  const tz = useUserTimeZone();

  const [step, setStep] = useState<Step>(1);
  const [closingDate, setClosingDate] = useState(() => isoDateInTz(new Date(), tz));
  const [newBuyerName, setNewBuyerName] = useState("");
  const [salePrice, setSalePrice] = useState("");
  const [assumedAmount, setAssumedAmount] = useState("");
  const [previewData, setPreviewData] = useState<ResaleClosingPreview | null>(
    null,
  );

  const loan = loanQuery.data;

  const inputsValid =
    !!closingDate &&
    newBuyerName.trim().length > 0 &&
    Number(salePrice) > 0 &&
    Number(assumedAmount) > 0 &&
    Number(salePrice) > Number(assumedAmount);

  async function handlePreview() {
    if (!loanId || !inputsValid) return;
    try {
      const data = await preview.mutateAsync({
        closingDate,
        newBuyerEntityName: newBuyerName.trim(),
        salePrice: Number(salePrice),
        assumedAmount: Number(assumedAmount),
      });
      setPreviewData(data);
      setStep(2);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function handleExecute() {
    if (!loanId || !previewData) return;
    try {
      const result = await execute.mutateAsync({
        closingDate,
        newBuyerEntityName: newBuyerName.trim(),
        salePrice: Number(salePrice),
        assumedAmount: Number(assumedAmount),
        expectedVersionId: previewData.versionId,
      });
      toast.success(
        `Resale committed — existing loan assumed by ${newBuyerName.trim()}; Spread Note ${result.spreadNoteLoanNumber} originated (${formatCurrency(result.spreadNotePrincipal)} principal). Bridge ends ${result.bridgePeriodExpiresAt}.`,
      );
      navigate(`/portfolio-admin/loans/${result.spreadNoteLoanId}`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  function useCurrentBalance() {
    if (loan?.currentBalance != null) {
      setAssumedAmount(String(loan.currentBalance));
    }
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <Button
            variant="text"
            size="sm"
            onClick={() => navigate(`/portfolio-admin/loans/${loanId}`)}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to loan
          </Button>
          <h1 className="mt-2 text-2xl font-semibold">
            Phoenix-Backed Resale Closing
          </h1>
          <p className="text-sm text-muted-foreground">
            Assume the existing Buyer Note under a new buyer and originate a
            Spread Note for the equity gain. 2% Assumption Fee fires WF1
            priority 22 to Phoenix Operating.
          </p>
        </div>
        <StepIndicator step={step} />
      </div>

      {step === 1 && (
        <Card className="space-y-4 p-6">
          <h2 className="text-lg font-semibold">1. Context &amp; inputs</h2>
          {loan ? (
            <div className="grid grid-cols-2 gap-3 rounded-md border bg-muted/30 p-4 text-sm">
              <div>
                <div className="text-muted-foreground">Loan</div>
                <div className="font-medium">{loan.loanNumber}</div>
              </div>
              <div>
                <div className="text-muted-foreground">Current balance</div>
                <div className="font-medium">
                  {formatCurrency(loan.currentBalance)}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground">Account state</div>
                <div className="font-medium">{loan.accountState ?? "—"}</div>
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-2 gap-4">
            <FormField label="Closing date">
              <Input
                type="date"
                value={closingDate}
                onChange={(e) => setClosingDate(e.target.value)}
              />
            </FormField>
            <FormField label="New buyer entity name">
              <Input
                value={newBuyerName}
                onChange={(e) => setNewBuyerName(e.target.value)}
                placeholder="e.g. Assumer Buyer LLC"
              />
            </FormField>
            <FormField label="Sale price">
              <Input
                type="number"
                value={salePrice}
                onChange={(e) => setSalePrice(e.target.value)}
                placeholder="1100000"
              />
            </FormField>
            <FormField label="Assumed amount (existing loan principal)">
              <div className="flex gap-2">
                <Input
                  type="number"
                  value={assumedAmount}
                  onChange={(e) => setAssumedAmount(e.target.value)}
                  placeholder="950000"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={useCurrentBalance}
                  disabled={!loan}
                >
                  Use current balance
                </Button>
              </div>
            </FormField>
          </div>

          <div className="rounded-md border bg-blue-50 p-3 text-xs text-blue-900">
            The Spread Note's principal is{" "}
            <strong>Sale Price − Assumed Amount</strong>. Sale price must
            exceed assumed amount or there is no equity gain to originate
            against.
          </div>

          <div className="flex justify-end">
            <Button
              onClick={handlePreview}
              disabled={!inputsValid || preview.isPending}
            >
              {preview.isPending ? "Computing..." : "Compute preview →"}
            </Button>
          </div>
        </Card>
      )}

      {step === 2 && previewData && (
        <Card className="space-y-4 p-6">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">2. Preview</h2>
            <Badge status="info">{previewData.loanState}</Badge>
          </div>

          <div className="grid grid-cols-2 gap-3 rounded-md border p-4 text-sm">
            <Figure
              label="Sale price"
              value={previewData.salePrice}
            />
            <Figure
              label="Assumed amount"
              value={previewData.assumedAmount}
            />
            <Figure
              label="Spread Note principal (Sale − Assumed)"
              value={previewData.spreadNotePrincipal}
              emphasis
            />
            <Figure
              label="Phoenix Assumption Fee (2% × assumed)"
              value={previewData.assumptionFee}
              emphasis
            />
          </div>

          <div className="rounded-md border bg-emerald-50 p-3 text-xs text-emerald-900">
            <strong>No YMP, no Prep, no Coord.</strong> Only the 2% Assumption
            Fee flows through WF1 (priority 22 → Phoenix Operating). The
            existing loan is <strong>assumed</strong> by{" "}
            <span className="font-semibold">{newBuyerName.trim()}</span>, not
            retired — it stays on the books in{" "}
            <span className="font-mono">performing_assumed</span> with its
            current balance.
          </div>

          {previewData.wf1Disbursements.length > 0 && (
            <div>
              <h3 className="mb-2 text-sm font-semibold">
                WF1 disbursements (assumption fee)
              </h3>
              <Table>
                <THead>
                  <TR>
                    <TH>#</TH>
                    <TH>Obligation</TH>
                    <TH>Recipient</TH>
                    <TH className="text-right">Amount</TH>
                  </TR>
                </THead>
                <TBody>
                  {previewData.wf1Disbursements.map((d, i) => (
                    <TR key={`wf1-${i}`}>
                      <TD>{d.priorityPosition}</TD>
                      <TD>{d.obligationTypeCode}</TD>
                      <TD>
                        {d.recipientDisplayName ?? d.recipientResolution}
                      </TD>
                      <TD className="text-right">{formatCurrency(d.amount)}</TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </div>
          )}

          <div className="space-y-2 rounded-md border bg-muted/30 p-4 text-sm">
            <h3 className="font-semibold">Spread Note preview</h3>
            <div className="grid grid-cols-2 gap-2">
              <Figure
                label="Loan number"
                stringValue={previewData.spreadNotePreview.loanNumber}
              />
              <Figure
                label="Original principal"
                value={previewData.spreadNotePreview.originalPrincipal}
              />
              <Figure
                label="Segment principal share (10 segments)"
                value={previewData.spreadNotePreview.segmentPrincipalShare}
              />
              <Figure
                label="Contract interest rate"
                stringValue={`${(previewData.spreadNotePreview.contractInterestRate * 100).toFixed(2)}%`}
              />
              <Figure
                label="Amortization months"
                stringValue={String(
                  previewData.spreadNotePreview.amortizationMonths,
                )}
              />
              <Figure
                label="Balloon month"
                stringValue={String(
                  previewData.spreadNotePreview.balloonMonth,
                )}
              />
              <Figure
                label="Seller of record"
                stringValue={previewData.spreadNotePreview.sellerOfRecord}
              />
              <Figure
                label="Borrower"
                stringValue={previewData.spreadNotePreview.borrower}
              />
              <Figure
                label="Bridge period ends"
                stringValue={previewData.spreadNotePreview.bridgePeriodExpiresAt}
                emphasis
              />
            </div>
          </div>

          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep(1)}>
              ← Back
            </Button>
            <Button onClick={() => setStep(3)}>Continue to confirm →</Button>
          </div>
        </Card>
      )}

      {step === 3 && previewData && (
        <Card className="space-y-4 p-6">
          <h2 className="text-lg font-semibold">3. Confirm Resale</h2>
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-900">
            Atomic operation. The existing loan{" "}
            <span className="font-semibold">({previewData.loanNumber})</span>{" "}
            will move to{" "}
            <span className="font-mono">performing_assumed</span>; a Spread
            Note{" "}
            <span className="font-semibold">
              ({previewData.spreadNotePreview.loanNumber})
            </span>{" "}
            of {formatCurrency(previewData.spreadNotePrincipal)} will be
            originated; the 12-month Resale Bridge Period starts today
            (ends {previewData.spreadNotePreview.bridgePeriodExpiresAt}); and
            the {formatCurrency(previewData.assumptionFee)} Assumption Fee
            will fire WF1 priority 22 to Phoenix Operating — all in one
            transaction.
          </div>

          <div className="flex justify-between">
            <Button variant="secondary" onClick={() => setStep(2)}>
              ← Back to preview
            </Button>
            <Button onClick={handleExecute} disabled={execute.isPending}>
              {execute.isPending ? (
                "Executing..."
              ) : (
                <>
                  <Check className="mr-2 h-4 w-4" /> Execute Resale
                </>
              )}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}

function StepIndicator({ step }: { step: Step }) {
  return (
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      {[1, 2, 3].map((n) => (
        <div
          key={n}
          className={
            n === step
              ? "rounded-full bg-primary px-3 py-1 font-semibold text-primary-foreground"
              : "rounded-full bg-muted px-3 py-1"
          }
        >
          Step {n}
        </div>
      ))}
    </div>
  );
}

function Figure({
  label,
  value,
  stringValue,
  emphasis,
}: {
  label: string;
  value?: number;
  stringValue?: string;
  emphasis?: boolean;
}) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div
        className={emphasis ? "text-base font-semibold" : "text-sm font-medium"}
      >
        {stringValue ?? (value !== undefined ? formatCurrency(value) : "—")}
      </div>
    </div>
  );
}

// Route entry — pre-contribution scope redirects deep links to the loan
// detail page while the stage-2 wizards are hidden.
export default function ResaleWizardRoute() {
  return (
    <PreContributionScopeRedirect>
      <ResaleWizard />
    </PreContributionScopeRedirect>
  );
}
